import React, { useState, useEffect, useMemo, useCallback } from "react";
import { withTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';

import {
    fetchContacts as fetch,
    seenContacts as seen,
    removeContacts as remove,
    undoContacts as undo,
    clearContacts as clear,
    addReply
} from './actions';

import api from '../../../api/api';
import { notificationIsView } from '../../../api/socket.io';
import MailBoxSidebar from "./MailBoxSidebar";
import MailBoxChatWindow from "./MailBoxChatWindow";
import UserModals from "../Users/components/UserModals";
import "./MailBox.css";

const MailBoxList = ({ t }) => {
    const dispatch = useDispatch();

    // Redux selectors
    const contactUs = useSelector(state => state.contactUs || {});
    const user = useSelector(state => state.user || {});

    // Default search date range: This Year
    const initialStart = moment().startOf('year').format('DD/MM/YYYY');
    const initialEnd = moment().endOf('year').format('DD/MM/YYYY');

    // Local state
    const [activeChat, setActiveChat] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [startDate, setStartDate] = useState(initialStart);
    const [endDate, setEndDate] = useState(initialEnd);
    const [dateRangeText, setDateRangeText] = useState(initialStart + " - " + initialEnd);
    const [sidebarOpen, setSidebarOpen] = useState(true);

    // Chat reply inputs & fetch cache
    const [newReplyText, setNewReplyText] = useState("");
    const [sendingReply, setSendingReply] = useState(false);
    const [repliesMap, setRepliesMap] = useState({});
    const [loadingReplies, setLoadingReplies] = useState(false);

    // UserModals integration state
    const [activeModalUser, setActiveModalUser] = useState(null);

    // Initial load
    useEffect(() => {
        dispatch(fetch(startDate, endDate));
    }, [dispatch, startDate, endDate]);

    // Aggregate contacts by user or by email (if user is null)
    const sessions = useMemo(() => {
        if (!contactUs || !contactUs.contacts) return [];

        const toRemove = contactUs.toRemove || [];
        const filteredContacts = contactUs.contacts.filter(c => !toRemove.includes(c.Id));

        const groups = {};

        filteredContacts.forEach(c => {
            let key = "";
            let name = "";
            let email = "";
            let photo = null;
            let country = null;
            let userObj = null;

            if (c.FromUser) {
                key = `user_${c.FromUser.Id}`;
                name = c.FromUser.Name || c.Sender || "";
                email = c.FromUser.Email || c.Email || "";
                photo = c.FromUser.Photo || null;
                country = c.Country || c.FromUser.Country || null;
                userObj = c.FromUser;
            } else {
                key = `email_${c.Email || c.Sender || c.Id}`;
                name = c.Sender || "";
                email = c.Email || "";
                photo = null;
                country = c.Country || null;
                userObj = null;
            }

            if (!groups[key]) {
                groups[key] = {
                    id: key,
                    name,
                    email,
                    photo,
                    country,
                    user: userObj,
                    messages: [],
                    unreadCount: 0
                };
            }

            groups[key].messages.push(c);
            if (!c.IsRead) {
                groups[key].unreadCount += 1;
            }
        });

        return Object.values(groups).map(group => {
            // Sort messages in group chronologically (oldest to newest)
            group.messages.sort((a, b) => {
                const parseDate = (dStr) => {
                    if (!dStr) return 0;
                    const parsed = moment(dStr, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss"], true);
                    return parsed.isValid() ? parsed.valueOf() : new Date(dStr).getTime() || 0;
                };
                return parseDate(a.CreationDate) - parseDate(b.CreationDate);
            });

            // Last message is the latest in sorted array
            const lastMessage = group.messages[group.messages.length - 1];
            let lastActivityTimestamp = 0;
            if (lastMessage) {
                const parsed = moment(lastMessage.CreationDate, ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss"], true);
                lastActivityTimestamp = parsed.isValid() ? parsed.valueOf() : new Date(lastMessage.CreationDate).getTime() || 0;
            }

            return {
                ...group,
                lastMessage,
                lastActivityTimestamp
            };
        }).sort((a, b) => b.lastActivityTimestamp - a.lastActivityTimestamp); // Sort sessions descending (newest activity first)
    }, [contactUs]);

    // Keep activeChat details in sync with updated sessions data (e.g. deletion, read flags)
    useEffect(() => {
        if (activeChat) {
            const currentSession = sessions.find(s => s.id === activeChat.id);
            if (currentSession) {
                const msgsChanged = currentSession.messages.length !== activeChat.messages.length ||
                    currentSession.messages.some((m, idx) => m.Id !== activeChat.messages[idx]?.Id || m.IsRead !== activeChat.messages[idx]?.IsRead);
                if (msgsChanged) {
                    setActiveChat(currentSession);
                }
            } else {
                setActiveChat(null);
            }
        }
    }, [sessions, activeChat]);

    // Fetch replies & mark unread messages as read when activeChat changes
    useEffect(() => {
        if (activeChat) {
            const fetchAllReplies = async () => {
                const contactsToFetch = activeChat.messages.filter(c => c.RepliesCount > 0 && !repliesMap[c.Id]);
                if (contactsToFetch.length === 0) return;

                setLoadingReplies(true);
                try {
                    await Promise.all(contactsToFetch.map(async (c) => {
                        const res = await api.get(`/ContactUs/${c.Id}`);
                        setRepliesMap(prev => ({
                            ...prev,
                            [c.Id]: res.data.data
                        }));
                    }));
                } catch (err) {
                    console.error("Failed fetching replies", err);
                } finally {
                    setLoadingReplies(false);
                }
            };

            fetchAllReplies();

            // Mark unread messages in this conversation as read
            const unreadContacts = activeChat.messages.filter(c => !c.IsRead);
            if (unreadContacts.length > 0) {
                unreadContacts.forEach(c => {
                    dispatch(seen(c.Id));
                });
                if (user?.user?.Id) {
                    notificationIsView("CONTACTUS", user.user.Id);
                }
            }
        }
    }, [activeChat, dispatch, user?.user?.Id, repliesMap]);

    // Operations
    const handleApplyDate = (event, picker) => {
        const start = picker.startDate.format('DD/MM/YYYY');
        const end = picker.endDate.format('DD/MM/YYYY');
        setDateRangeText(start + " - " + end);
        setStartDate(start);
        setEndDate(end);
        dispatch(fetch(start, end));
    };

    const handleSync = () => {
        dispatch(fetch(startDate, endDate));
    };

    const handleSendReply = async (e) => {
        if (e) e.preventDefault();
        const redactorVal = window.getRedactor("#ReplyRedactor");
        const trimmedVal = redactorVal ? redactorVal.trim() : "";
        if (!trimmedVal || trimmedVal === "<p></p>" || trimmedVal === "<p><br></p>" || !activeChat) return;

        const latestContact = activeChat.messages[activeChat.messages.length - 1];
        if (!latestContact) return;

        setSendingReply(true);
        const html = trimmedVal;

        let _toastId = toast(t('Processing'), {
            position: "bottom-left",
            autoClose: false
        });
        toast.update(_toastId, {
            render: () => <div> <i className="fa-spin fas fa-spinner"></i> {t('Processing')} </div>,
            autoClose: false,
            closeOnClick: false,
        });

        try {
            await api.post(`/ContactUs/${latestContact.Id}`, { Body: html });
            dispatch(addReply(latestContact.Id));

            // Immediately load the new replies
            const res = await api.get(`/ContactUs/${latestContact.Id}`);
            setRepliesMap(prev => ({
                ...prev,
                [latestContact.Id]: res.data.data
            }));

            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-paper-plane"></i> {t('Pages.MailBox.toast.Send.Title')}</div> <div className="h6">{t('Pages.MailBox.toast.Send.Body')}</div></div>,
                type: toast.TYPE.SUCCESS,
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });

            window.cleanRedactor("#ReplyRedactor");
            setNewReplyText("");
        } catch (err) {
            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                type: toast.TYPE.ERROR,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        } finally {
            setSendingReply(false);
        }
    };

    const deleteMail = (id) => {
        let _toastId = toast(t('Processing'), {
            position: "bottom-left",
            autoClose: false
        });
        toast.update(_toastId, {
            render: () => <div> <i className="fa-spin fas fa-spinner"></i> {t('Processing')} </div>,
            autoClose: false,
            closeOnClick: false,
        });

        api.delete(`/ContactUs/${id}`).then(response => {
            dispatch(clear(id));

            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-exclamation-triangle"></i> {t('Pages.MailBox.toast.Delete.Title')}</div> <div className="h6">{t('Pages.MailBox.toast.Delete.Body')}</div></div>,
                type: toast.TYPE.SUCCESS,
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        }).catch(error => {
            toast.update(_toastId, {
                render: () => <div><div className="h5"> <i className="icon fal fa-wifi-slash"></i> {t('Error.NoConnection.Title')}</div> <div className="h6">{t('Error.NoConnection.Body')}</div></div>,
                type: toast.TYPE.ERROR,
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        });
    };

    const handleShowDelete = (id) => {
        if (!id) return;
        dispatch(remove(id));

        let _toastId = toast(t('Processing'), {
            position: "bottom-left",
            autoClose: false
        });

        toast.update(_toastId, {
            render: () => <div>
                <i className="fa-spin fas fa-spinner"></i> {t('DeleteProcessing')}
                <div className="row row-cols-2">
                    <div className="col">
                        <button type="button" className="btn btn-danger btn-block " onClick={() => { dispatch(undo(id)); toast.dismiss(_toastId); }}>
                            <i className="icon fal fa-trash-undo"></i> {t('undo')}
                        </button>
                    </div>
                    <div className="col">
                        <button type="button" className="btn btn-light btn-block " onClick={() => { deleteMail(id); toast.dismiss(_toastId); }}>
                            <i className="icon fal fa-recycle"></i> {t('confirm')}
                        </button>
                    </div>
                </div>
            </div>,
            type: toast.TYPE.DARK,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true
        });
    };

    // Predefined date picker ranges
    const pickerRanges = useMemo(() => {
        const ranges = {};
        ranges[t("range.Today")] = [moment().toDate(), moment().toDate()];
        ranges[t("range.Yesterday")] = [moment().subtract(1, 'days'), moment().subtract(1, 'days')];
        ranges[t("range.Last7Days")] = [moment().subtract(6, 'days'), moment()];
        ranges[t("range.Last30Days")] = [moment().subtract(29, 'days'), moment()];
        ranges[t("range.ThisMonth")] = [moment().startOf('month'), moment().endOf('month')];
        ranges[t("range.LastMonth")] = [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')];
        ranges[t("range.ThisYear")] = [moment().startOf('year'), moment().endOf('year')];
        ranges[t("range.YearsAgo")] = [moment().subtract(1, 'years').startOf('year'), moment().subtract(1, 'years').endOf('year')];
        return ranges;
    }, [t]);

    // =============================
    // UserModals Operations Callbacks
    // =============================

    const refreshData = useCallback(() => {
        dispatch(fetch(startDate, endDate));
    }, [startDate, endDate, dispatch]);

    const loadingChats = !contactUs || !contactUs.isLoaded;

    return (
        <>
            <Helmet>
                <title>{t('Pages.MailBox.title')} | {t('alarabeyya')}</title>
                <script src={process.env.PUBLIC_URL + '/assets/redactor/redactor.js?v=' + process.env.REACT_APP_VERSION} ></script>
                <link rel="stylesheet" type="text/css" href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap-colorpicker/2.5.1/css/bootstrap-colorpicker.min.css" />
                <script src="https://cdnjs.cloudflare.com/ajax/libs/bootstrap-colorpicker/2.5.1/js/bootstrap-colorpicker.js"></script>
            </Helmet>

            <div className="whatsapp-page-wrapper">
                {!sidebarOpen && (
                    <div
                        className="mobile-sidebar-toggle-btn d-md-none"
                        onClick={() => setSidebarOpen(true)}
                        title={t("Pages.Chatbot.OpenMenu") || "فتح القائمة"}
                    >
                        <i className="fal fa-bars"></i>
                    </div>
                )}
                <div className="whatsapp-container">
                    <MailBoxSidebar
                        chats={sessions}
                        activeChat={activeChat}
                        loadingChats={loadingChats}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                        onSelectChat={setActiveChat}
                        onSyncClick={handleSync}
                        dateRangeText={dateRangeText}
                        handleApplyDate={handleApplyDate}
                        ranges={pickerRanges}
                        onUserClick={setActiveModalUser}
                        t={t}
                    />

                    {activeChat ? (
                        <MailBoxChatWindow
                            activeChat={activeChat}
                            messages={activeChat.messages}
                            repliesMap={repliesMap}
                            loadingReplies={loadingReplies}
                            sendingReply={sendingReply}
                            newReplyText={newReplyText}
                            setNewReplyText={setNewReplyText}
                            onSendReply={handleSendReply}
                            onDeleteContact={handleShowDelete}
                            onBackToList={() => setActiveChat(null)}
                            onUserClick={setActiveModalUser}
                            user={user}
                            t={t}
                        />
                    ) : (
                        <div className="whatsapp-welcome-screen">
                            <div className="whatsapp-welcome-content">
                                <i className="fad fa-envelope-open-text text-primary" style={{ fontSize: '72px' }}></i>
                                <h4 className="mt-3">{t("Pages.MailBox.title")}</h4>
                                <p className="text-muted mt-2">{t("Pages.MailBox.welcomeText", "اختر محادثة من القائمة لعرض الرسائل والردود")}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* User Profile Modals Integration */}
            <UserModals
                activeUser={activeModalUser}
                onClose={() => setActiveModalUser(null)}
                onSuccess={refreshData}
                t={t}
            />
        </>
    );
};

export default withTranslation('common')(MailBoxList);
